import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaRobot, FaCode, FaMobileAlt, FaCloud, FaBug, FaDatabase,
  FaArrowRight, FaCheck, FaCogs, FaSearch, FaRocket, FaSitemap, FaLightbulb,
} from 'react-icons/fa';
import PageTransition from '../components/PageTransition';
import {
  fadeInUp, scaleIn,
  staggerContainer, staggerContainerSlow, viewport,
} from '../utils/animations';

/* ─── Static services data ─────────────────────────────────── */
export const services = [
  {
    slug: 'ai-automation',
    icon: FaRobot,
    title: 'AI & Automation',
    short: 'Build adaptive AI agents and intelligent automation that transforms operations.',
    tagline: 'Intelligent systems that learn, adapt, and scale with your business.',
    description:
      'We design and deploy end-to-end AI solutions, from custom machine learning models to fully autonomous agent pipelines. Whether you need to automate repetitive workflows, extract insights from unstructured data, or build conversational AI, our team delivers production-grade systems that integrate seamlessly with your existing stack.',
    features: ['Machine Learning Models', 'NLP & Chatbots', 'Process Automation', 'Computer Vision'],
    gradient: 'from-gray-700 to-gray-900',
    benefits: [
      { title: 'Reduce Operational Costs', desc: 'Automate high-volume, repetitive tasks and free your team for higher-value work.' },
      { title: 'Scale Without Headcount', desc: 'AI agents handle spikes in demand 24/7 without proportional hiring.' },
      { title: 'Faster Decision-Making', desc: 'Real-time ML inference surfaces insights at the moment they matter.' },
      { title: 'Competitive Edge', desc: 'Deploy capabilities your competitors can\'t replicate overnight.' },
    ],
    process: [
      { num: '01', title: 'Data Audit & Strategy', desc: 'We assess your data landscape, identify automation opportunities, and define measurable success metrics.' },
      { num: '02', title: 'Model Design & Training', desc: 'Custom models trained on your domain data, not generic off-the-shelf solutions.' },
      { num: '03', title: 'Integration & Deployment', desc: 'Production-ready deployment with APIs, monitoring dashboards, and fallback mechanisms.' },
      { num: '04', title: 'Continuous Learning', desc: 'Models that improve over time via feedback loops and scheduled retraining pipelines.' },
    ],
    technologies: ['Python', 'TensorFlow', 'PyTorch', 'LangChain', 'OpenAI API', 'Hugging Face', 'FastAPI', 'Celery'],
    outcomes: ['Up to 70% reduction in manual workload', 'Sub-100ms inference latency', '24/7 autonomous operation', 'Explainable AI reporting'],
  },
  {
    slug: 'product-engineering',
    icon: FaCode,
    title: 'Product Engineering',
    short: 'Full-stack teams that ship scalable products at unicorn velocity.',
    tagline: 'From zero to production, with speed, quality, and zero compromise.',
    description:
      'Our cross-functional engineering squads take products from concept to launch, then keep iterating. We combine deep technical expertise with product thinking to build systems that are resilient, maintainable, and ready to scale. We embed directly into your team or work as an independent unit, whichever fits your organisation.',
    features: ['Full-Stack Development', 'Microservices', 'API Design', 'Scalable Architecture'],
    gradient: 'from-gray-600 to-gray-900',
    benefits: [
      { title: 'Ship Faster', desc: 'Agile sprints with weekly demos keep momentum high and reduce long feedback cycles.' },
      { title: 'Built to Scale', desc: 'Architecture decisions made with growth in mind from day one.' },
      { title: 'Reduced Technical Debt', desc: 'Clean code, peer reviews, and structured documentation prevent costly rewrites.' },
      { title: 'Flexible Engagement', desc: 'Augment your team, build a dedicated squad, or hand off a complete product.' },
    ],
    process: [
      { num: '01', title: 'Requirements & Scoping', desc: 'We translate business goals into technical specifications with clear milestones.' },
      { num: '02', title: 'Architecture Design', desc: 'System design blueprints, database schemas, and API contracts agreed upfront.' },
      { num: '03', title: 'Agile Development', desc: 'Two-week sprints, daily standups, and continuous integration from the first commit.' },
      { num: '04', title: 'Launch & Handover', desc: 'Zero-downtime release, runbooks, and optional long-term maintenance packages.' },
    ],
    technologies: ['React', 'Next.js', 'Node.js', 'TypeScript', 'PostgreSQL', 'MongoDB', 'Redis', 'Docker'],
    outcomes: ['Production launch in 8-16 weeks', '99.9% uptime SLA', 'Comprehensive API documentation', 'Full ownership transfer'],
  },
  {
    slug: 'mobile-development',
    icon: FaMobileAlt,
    title: 'Mobile Development',
    short: 'Native iOS & Android apps with React Native for exceptional UX.',
    tagline: 'Delightful mobile experiences that users open every day.',
    description:
      'We craft high-performance mobile applications that feel native on both iOS and Android from a single, maintainable codebase. Our UX-first approach ensures every interaction is intuitive, fast, and polished, because app store success depends on the details users feel, not just features listed on the store page.',
    features: ['Cross-Platform Apps', 'Native Performance', 'App Store Optimisation', 'Real-time Sync'],
    gradient: 'from-gray-700 to-gray-900',
    benefits: [
      { title: 'Single Codebase, Dual Platform', desc: 'Ship on iOS and Android simultaneously without doubling development cost.' },
      { title: 'Native-Level Performance', desc: 'React Native bridges to native APIs, no compromise on speed or capability.' },
      { title: 'Faster Updates', desc: 'Over-the-air updates push fixes and features without App Store delays.' },
      { title: 'Offline-First Architecture', desc: 'Apps that work reliably even with intermittent connectivity.' },
    ],
    process: [
      { num: '01', title: 'UX Research & Wireframing', desc: 'User journey mapping and interactive prototypes reviewed with real users before a line of code is written.' },
      { num: '02', title: 'UI Design', desc: 'Pixel-perfect, accessible designs aligned to Apple HIG and Material Design guidelines.' },
      { num: '03', title: 'Development & Testing', desc: 'React Native development with automated testing on real device farms.' },
      { num: '04', title: 'App Store Launch', desc: 'Store listings, ASO, screenshot assets, and guided review submission.' },
    ],
    technologies: ['React Native', 'Expo', 'TypeScript', 'Redux Toolkit', 'Firebase', 'Stripe SDK', 'Push Notifications', 'Fastlane'],
    outcomes: ['iOS & Android from one codebase', '60fps smooth animations', 'App store approval support', 'Crash rate < 0.1%'],
  },
  {
    slug: 'cloud-devops',
    icon: FaCloud,
    title: 'Cloud & DevOps',
    short: 'Scalable infrastructure and CI/CD pipelines for seamless delivery.',
    tagline: 'Infrastructure that scales automatically and deploys without fear.',
    description:
      'We design cloud-native architectures and DevOps pipelines that make deploying software a non-event. From container orchestration to multi-region failover, we build the infrastructure layer that lets your product team move fast safely, with full observability, automated rollbacks, and infrastructure-as-code that lives in your Git repository.',
    features: ['AWS / Azure / GCP', 'CI/CD Pipelines', 'Infrastructure as Code', 'Kubernetes'],
    gradient: 'from-gray-600 to-gray-900',
    benefits: [
      { title: 'Deploy Anytime, Safely', desc: 'Automated CI/CD pipelines with staged rollouts and instant rollback on anomaly.' },
      { title: 'Cost Optimisation', desc: 'Right-sized infrastructure and auto-scaling eliminate idle resource spend.' },
      { title: 'Developer Productivity', desc: 'Standardised environments eliminate "works on my machine" for good.' },
      { title: 'Full Observability', desc: 'Unified logs, metrics, and traces across every service and environment.' },
    ],
    process: [
      { num: '01', title: 'Infrastructure Audit', desc: 'Review existing architecture, identify bottlenecks, security gaps, and cost inefficiencies.' },
      { num: '02', title: 'Cloud Architecture Design', desc: 'Multi-AZ, auto-scaling blueprints tailored to your traffic patterns and compliance needs.' },
      { num: '03', title: 'IaC & Pipeline Setup', desc: 'Terraform/Pulumi modules + GitHub Actions or GitLab CI pipelines configured and tested.' },
      { num: '04', title: 'Monitoring & Runbooks', desc: 'Alerting thresholds, on-call playbooks, and SLA dashboards handed over to your team.' },
    ],
    technologies: ['AWS', 'GCP', 'Azure', 'Kubernetes', 'Terraform', 'Helm', 'GitHub Actions', 'Datadog', 'Prometheus'],
    outcomes: ['< 5 min deployment cycles', '99.99% uptime architecture', '30-50% infrastructure cost reduction', 'SOC 2 compliant pipelines'],
  },
  {
    slug: 'qa-testing',
    icon: FaBug,
    title: 'QA & Testing',
    short: 'End-to-end quality assurance with automated testing pipelines.',
    tagline: 'Ship with confidence, bugs caught before your users ever see them.',
    description:
      'Our QA engineers embed into your development cycle to build comprehensive automated test suites, conduct thorough manual exploratory testing, and implement continuous quality gates in your CI/CD pipeline. We treat quality as an engineering problem, not an afterthought, delivering robust coverage that lets your team release faster, not slower.',
    features: ['Automated Testing', 'Performance Testing', 'Security Audits', 'CI Integration'],
    gradient: 'from-gray-700 to-gray-900',
    benefits: [
      { title: 'Release Faster, Not Riskier', desc: 'Automated regression suites catch breakages in minutes, enabling confident daily deployments.' },
      { title: 'Lower Bug Fix Costs', desc: 'Bugs caught in CI cost 10x less to fix than bugs found in production.' },
      { title: 'Performance Baselines', desc: 'Load testing establishes thresholds before launch, not after a 3am incident.' },
      { title: 'Security Coverage', desc: 'OWASP-aligned security testing integrated into your pipeline from day one.' },
    ],
    process: [
      { num: '01', title: 'Test Strategy & Planning', desc: 'Risk-based test planning that prioritises coverage where failure is most costly.' },
      { num: '02', title: 'Framework Setup', desc: 'Playwright, Cypress, or Jest suites scaffolded and integrated with your CI pipeline.' },
      { num: '03', title: 'Test Execution & Reporting', desc: 'Parallel test runs with detailed failure reporting, screenshots, and traces.' },
      { num: '04', title: 'Continuous Improvement', desc: 'Regular test suite audits to remove flaky tests and maintain signal quality.' },
    ],
    technologies: ['Playwright', 'Cypress', 'Jest', 'Selenium', 'k6', 'OWASP ZAP', 'Postman', 'GitHub Actions'],
    outcomes: ['80%+ automated test coverage', 'Flaky test rate < 2%', 'Load testing up to 100k concurrent users', 'Zero critical bugs at launch'],
  },
  {
    slug: 'data-engineering',
    icon: FaDatabase,
    title: 'Data Engineering',
    short: 'Robust data pipelines and analytics that drive informed decisions.',
    tagline: 'Transform raw data into your most valuable strategic asset.',
    description:
      'We build the data infrastructure that powers modern businesses, from ingestion and transformation to warehousing and real-time dashboards. Whether you are migrating from spreadsheets to a modern data stack or scaling an existing platform, we design pipelines that are reliable, observable, and maintainable by your team long after we hand over.',
    features: ['Data Warehousing', 'Real-time Analytics', 'ETL Pipelines', 'Data Visualisation'],
    gradient: 'from-gray-600 to-gray-900',
    benefits: [
      { title: 'Single Source of Truth', desc: 'Unified data model across all business units eliminates conflicting reports.' },
      { title: 'Real-Time Decisions', desc: 'Streaming pipelines surface operational insights within seconds, not hours.' },
      { title: 'Self-Serve Analytics', desc: 'Business teams query data directly without engineering bottlenecks.' },
      { title: 'Data Governance', desc: 'Lineage tracking, PII masking, and access controls built into the pipeline.' },
    ],
    process: [
      { num: '01', title: 'Data Discovery', desc: 'Catalogue all data sources, map relationships, and document quality gaps.' },
      { num: '02', title: 'Architecture Design', desc: 'Choose the right warehouse, lakehouse, or hybrid architecture for your use case.' },
      { num: '03', title: 'Pipeline Development', desc: 'dbt models, Airflow DAGs, and Kafka streams built with CI/CD and full test coverage.' },
      { num: '04', title: 'Dashboard & Handover', desc: 'Business-ready dashboards and runbooks so your team owns the platform confidently.' },
    ],
    technologies: ['dbt', 'Apache Airflow', 'Kafka', 'Snowflake', 'BigQuery', 'Spark', 'Redshift', 'Looker', 'Metabase'],
    outcomes: ['< 5 min data freshness SLA', 'Petabyte-scale architecture', '100% pipeline observability', 'GDPR-compliant data handling'],
  },
];

/* ─── Process steps ────────────────────────────────────────── */
const process = [
  { num: '01', icon: FaSearch,  title: 'Discovery',   desc: 'Deep-dive into your business goals, users, and technical landscape.' },
  { num: '02', icon: FaSitemap, title: 'Architecture', desc: 'Design a robust technical blueprint with clear milestones and deliverables.' },
  { num: '03', icon: FaCode,    title: 'Development',  desc: 'Agile sprints, weekly demos, and continuous feedback loops.' },
  { num: '04', icon: FaRocket,  title: 'Launch & Scale', desc: 'Zero-downtime deployments, monitoring, and ongoing optimisation.' },
];

const Services = () => (
  <PageTransition>
    <div className="bg-transparent min-h-screen">

      {/* ── Hero ────────────────────────────────────────────── */}
      <section className="relative pt-36 pb-24 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
        <div className="spotlight w-[600px] h-[600px] top-1/2 left-1/2" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div variants={fadeInUp} className="inline-flex mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 backdrop-blur-sm border border-gray-700 rounded-full badge-glow">
                <FaCogs className="text-gray-400 text-xs" />
                <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">What We Offer</span>
              </div>
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
              <span className="text-white">Comprehensive</span>
              <br />
              <span className="animated-gradient-text">Technical Solutions</span>
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-gray-400 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10">
              From concept to deployment, we provide end-to-end engineering services
              that help businesses scale and innovate in the digital age.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="group flex items-center gap-2 px-8 py-3.5 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:-translate-y-0.5 shine-hover"
              >
                Get a Free Consultation
                <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/projects"
                className="group flex items-center gap-2 px-8 py-3.5 border border-gray-700 text-gray-300 font-semibold rounded-xl hover:border-gray-500 hover:text-white transition-all duration-300 hover:-translate-y-0.5"
              >
                View Case Studies
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Services Grid ───────────────────────────────────── */}
      <section className="relative py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainerSlow}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          >
            {services.map((svc, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                whileHover={{ y: -8, transition: { duration: 0.25 } }}
                className="group relative glass-card rounded-2xl p-8 card-hover-lift shine-hover"
              >
                {/* Glow on hover */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gray-700/0 to-gray-600/0 group-hover:from-gray-700/10 group-hover:to-gray-600/5 transition-all duration-500 pointer-events-none" />

                {/* Icon */}
                <div className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${svc.gradient} border border-gray-700 flex items-center justify-center mb-6 group-hover:border-gray-500 transition-colors duration-300`}>
                  <svc.icon className="text-3xl text-gray-300 group-hover:text-white transition-colors duration-300" />
                </div>

                <h3 className="text-white font-bold text-xl mb-3">{svc.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6">{svc.short}</p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {svc.features.map((f, j) => (
                    <motion.li
                      key={j}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={viewport}
                      transition={{ delay: j * 0.08 }}
                      className="flex items-center gap-2 text-xs text-gray-500"
                    >
                      <FaCheck className="text-gray-600 flex-shrink-0" />
                      {f}
                    </motion.li>
                  ))}
                </ul>

                <Link
                  to={`/services/${svc.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 group-hover:text-white transition-colors duration-300 link-underline"
                >
                  Learn More <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Process ─────────────────────────────────────────── */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 dot-pattern pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-white mb-4">
              How We <span className="animated-gradient-text">Work</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-gray-400 max-w-lg mx-auto">
              A lean, transparent process designed to move fast and deliver quality every step.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {/* Connecting line */}
            <div className="hidden lg:block absolute top-12 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent" />

            {process.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewport}
                transition={{ delay: i * 0.15, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                className="relative text-center group"
              >
                {/* Step number circle */}
                <div className="relative inline-flex w-20 h-20 rounded-full bg-gray-900/70 border border-gray-700 items-center justify-center mb-6 mx-auto group-hover:border-gray-500 transition-colors duration-300 animate-pulse-glow">
                  <step.icon className="text-2xl text-gray-400 group-hover:text-white transition-colors duration-300" />
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 border border-gray-600 text-xs font-black text-white flex items-center justify-center">
                    {step.num}
                  </span>
                </div>

                <h3 className="text-white font-bold text-lg mb-3">{step.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────── */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/0 via-gray-800/25 to-gray-900/0 pointer-events-none" />
        <div className="spotlight w-96 h-96 top-1/2 left-1/2" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="glass-card rounded-3xl p-12 md:p-16 text-center max-w-3xl mx-auto"
          >
            <motion.div variants={scaleIn} className="flex justify-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gray-800 border border-gray-700 flex items-center justify-center">
                <FaLightbulb className="text-gray-300 text-2xl" />
              </div>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-white mb-4">
              Have a project in mind?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-gray-400 text-lg mb-10">
              We'd love to hear about it. Let's schedule a free 30-minute discovery call.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="group flex items-center gap-2 px-10 py-4 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:-translate-y-1 shine-hover"
              >
                Book a Call
                <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  </PageTransition>
);

export default Services;


